import { useUIStore } from '@/state/store';
import type { ReachPrefKey } from '@/state/store';
import { useEffect, useState, useCallback } from 'react';
import { apiFetch } from '@/lib/auth';
import { DeviceCheckPanel } from './CameraPreview';
import { FolderTargetsSettings } from './FolderTargetsSettings';
import { SealCertSettings } from './SealCertSettings';
import { useReachability, type Capabilities, type CapabilityState } from '@/hooks/useReachability';
import { RINGTONES, playRingtonePreview } from '@/hooks/useAudio';
import { connectHeadset, disconnectHeadset, headsetState, onHeadsetChange, busylightAvailable, LS_BUSYLIGHT, LS_FAC_AUX, LS_FAC_READY } from '@/lib/headsetTelephony';

function Toggle({ on, onClick }: { on: boolean; onClick: () => void }) {
  return <div className={`toggle ${on ? 'on' : ''}`} onClick={onClick}></div>;
}

function Row({ name, desc, children }: { name: string; desc: string; children: React.ReactNode }) {
  return (
    <div className="settings-row">
      <div className="sr-info">
        <div className="sr-name">{name}</div>
        <div className="sr-desc">{desc}</div>
      </div>
      {children}
    </div>
  );
}

// pre142/pre143: Telefonie-Verknüpfung pro Anbieter (Teams=graph, Webex=webex).
// Liest den Status und bietet Verbinden (OAuth-Redirect) bzw. Trennen. Inert, wenn
// serverseitig kein Adapter konfiguriert ist (configured=false).
const teamsBtnStyle: React.CSSProperties = {
  background: 'transparent', border: '1px solid var(--hairline)', color: 'var(--fg)',
  fontFamily: 'var(--font)', fontSize: 12, padding: '6px 14px', borderRadius: 999, cursor: 'pointer',
};

function ProviderLinkRow({ provider, label }: { provider: 'graph' | 'webex'; label: string }) {
  const [st, setSt] = useState<{ loading: boolean; configured: boolean; linked: boolean; busy?: boolean; err?: string }>({
    loading: true, configured: false, linked: false,
  });

  async function refresh() {
    try {
      const r = await apiFetch(`/api/telephony/${provider}/status`, {}, { noLogoutOn401: true } as any);
      if (r.ok) { const j = await r.json(); setSt({ loading: false, configured: !!j.configured, linked: !!j.linked }); }
      else setSt({ loading: false, configured: false, linked: false });
    } catch { setSt({ loading: false, configured: false, linked: false }); }
  }
  useEffect(() => { void refresh(); /* eslint-disable-next-line */ }, []);

  async function connect() {
    setSt((s) => ({ ...s, busy: true, err: undefined }));
    try {
      const r = await apiFetch(`/api/telephony/${provider}/connect`);
      if (!r.ok) { const t = await r.text().catch(() => ''); setSt((s) => ({ ...s, busy: false, err: t || 'Fehler' })); return; }
      const j = await r.json();
      if (j?.authorizeUrl) { window.location.href = j.authorizeUrl; return; }
      setSt((s) => ({ ...s, busy: false, err: 'keine Login-URL erhalten' }));
    } catch (e) { setSt((s) => ({ ...s, busy: false, err: String(e) })); }
  }

  async function unlink() {
    setSt((s) => ({ ...s, busy: true }));
    try { await apiFetch(`/api/telephony/${provider}/unlink`, { method: 'POST' }); } catch { /* */ }
    await refresh();
  }

  if (st.loading) return <Row name={label} desc="Status wird geladen …"><span style={{ color: 'var(--muted)', fontSize: 12 }}>…</span></Row>;
  if (!st.configured) return <Row name={label} desc="Serverseitig nicht konfiguriert"><span style={{ color: 'var(--muted)', fontSize: 12 }}>–</span></Row>;
  if (st.linked) return (
    <Row name={`${label} verbunden ✓`} desc="Telefonstatus koppelt automatisch ans Routing">
      <button onClick={unlink} disabled={st.busy} style={teamsBtnStyle}>{st.busy ? '…' : 'Trennen'}</button>
    </Row>
  );
  return (
    <>
      <Row name={`${label} verbinden`} desc="Telefonstatus automatisch übernehmen (einmaliger Login)">
        <button onClick={connect} disabled={st.busy} style={teamsBtnStyle}>{st.busy ? '…' : 'Verbinden'}</button>
      </Row>
      {st.err && <div className="sr-desc" style={{ color: 'var(--danger)', padding: '2px 0' }}>{st.err}</div>}
    </>
  );
}

function TelephonyLinkSection() {
  return (
    <div className="settings-section">
      <div className="settings-section-title">Telefonie-Kopplung</div>
      <div style={{ fontSize: 12.5, color: 'var(--muted,#8aa)', margin: '2px 0 10px', lineHeight: 1.5 }}>
        Verbindet den Telefonstatus dieses Arbeitsplatzes mit der Videoberatung: Wer telefoniert,
        bekommt keine Videocalls — und umgekehrt. Je nach Telefonie-Umgebung stehen drei Wege zur
        Verfügung; sie können auch kombiniert werden.
      </div>
      <ProviderLinkRow provider="graph" label="Microsoft Teams" />
      <ProviderLinkRow provider="webex" label="Cisco Webex" />
      <HeadsetSettings />
      <ConnectorUserSection />
    </div>
  );
}

// ── pre251: Anlagen-Konnektor (CSTA) — Benutzer-Sicht ────────────────────────
function ConnectorUserSection() {
  const [st, setSt] = useState<{ enabled: boolean; connectorOnline: boolean; extension: string; agentId: string; monitored: boolean } | null>(null);
  const [ext, setExt] = useState('');
  const [agentId, setAgentId] = useState('');
  const [msg, setMsg] = useState('');
  const load = useCallback(async () => {
    try { const r = await apiFetch('/api/telephony-connector/me'); if (r.ok) { const j = await r.json(); setSt(j); setExt(j.extension); setAgentId(j.agentId); } } catch { /* */ }
  }, []);
  useEffect(() => { void load(); }, [load]);
  if (!st || !st.enabled) return null; // erscheint erst, wenn der Admin den Konnektor freigeschaltet hat
  const save = async () => {
    setMsg('');
    try {
      const r = await apiFetch('/api/telephony-connector/me', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ extension: ext, agentId }) });
      setMsg(r.ok ? 'Gespeichert.' : 'Speichern fehlgeschlagen.');
      void load();
    } catch { setMsg('Verbindung fehlgeschlagen.'); }
    setTimeout(() => setMsg(''), 3000);
  };
  return (
    <div style={{ marginTop: 18, paddingTop: 14, borderTop: '1px solid var(--hairline,#333)' }}>
      <div style={{ fontWeight: 600, fontSize: 13.5, marginBottom: 6 }}>Telefonanlage des Instituts (über Konnektor)</div>
      <div style={{ fontSize: 12.5, color: 'var(--muted,#8aa)', lineHeight: 1.55, marginBottom: 10 }}>
        <b>Was ist das?</b> Ihr Institut hat einen Telefonie-Konnektor eingerichtet, der die hauseigene
        Telefonanlage (z. B. Avaya) mit der Videoberatung verbindet. <b>Was müssen Sie tun?</b> Tragen Sie
        hier Ihre eigene Nebenstelle (und im Kundenservice-Center Ihre Agenten-Kennung) ein. Danach passiert
        alles automatisch: <b>Telefonieren Sie, erhalten Sie keine Videocalls.</b> Nehmen Sie eine
        Videoberatung an, meldet die Anlage Sie für deren Dauer aus der Anrufverteilung ab (AUX) und danach
        wieder an. Es werden keine Gesprächsinhalte übertragen — nur „frei/besetzt".
      </div>
      <div style={{ fontSize: 12.5, marginBottom: 10 }}>
        Konnektor-Status:{' '}
        {st.connectorOnline
          ? <b style={{ color: '#7ee787' }}>verbunden</b>
          : <b style={{ color: '#ffb74d' }}>derzeit nicht verbunden</b>}
        {st.extension && st.connectorOnline && (
          <> {' · '} Ihre Nebenstelle {st.extension}: {st.monitored ? <b style={{ color: '#7ee787' }}>wird überwacht</b> : <span>Überwachung wird eingerichtet …</span>}</>
        )}
      </div>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
        <label style={{ fontSize: 12.5 }}>Nebenstelle:{' '}
          <input value={ext} onChange={(e) => setExt(e.target.value)} placeholder="z. B. 4711"
            style={{ padding: '6px 9px', borderRadius: 6, background: 'var(--bg-elev-2,#20262b)', color: 'inherit', border: '1px solid var(--hairline,#444)', width: 110, fontSize: 13 }} />
        </label>
        <label style={{ fontSize: 12.5 }}>Agenten-Kennung (nur KSC):{' '}
          <input value={agentId} onChange={(e) => setAgentId(e.target.value)} placeholder="optional"
            style={{ padding: '6px 9px', borderRadius: 6, background: 'var(--bg-elev-2,#20262b)', color: 'inherit', border: '1px solid var(--hairline,#444)', width: 110, fontSize: 13 }} />
        </label>
        <button onClick={() => void save()}
          style={{ padding: '7px 12px', borderRadius: 8, border: 'none', background: 'var(--accent,#e2001a)', color: '#fff', fontWeight: 600, cursor: 'pointer', fontSize: 12.5 }}>Speichern</button>
        {msg && <span style={{ fontSize: 12, color: 'var(--muted,#8aa)' }}>{msg}</span>}
      </div>
    </div>
  );
}

function SizeRow({ label, min, max, value, onChange, unit }: {
  label: string; min: number; max: number; value: number; onChange: (v: number) => void; unit: string;
}) {
  return (
    <div className="tx-cap-size-row">
      <span className="label">{label}</span>
      <input type="range" min={min} max={max} value={value} onChange={(e) => onChange(Number(e.target.value))} />
      <span className="val">{value}{unit}</span>
    </div>
  );
}

// pre169: Übersetzungs-Einstellungen, die vorher in der rechten Leiste standen.
// Hierher gehören die selten/dauerhaft gesetzten Werte: die Berater-Sprache
// (ändert sich nie) und die Untertitel-Größen. Die gesprächsspezifischen
// Steuerungen (Stele-Sprache, Kunden-Sprachauswahl, Nachricht) liegen im
// Dropdown neben dem Übersetzungs-Button in der Anruf-Leiste.
function TranslationSettings() {
  const capSizeCustomer = useUIStore((s) => s.capSizeCustomer);
  const capSizeSelf = useUIStore((s) => s.capSizeSelf);
  const capWidthPercent = useUIStore((s) => s.capWidthPercent);
  const setCapSize = useUIStore((s) => s.setCapSize);
  const setCapWidth = useUIStore((s) => s.setCapWidth);
  return (
    <div className="settings-section">
      <div className="settings-section-title">Übersetzung</div>
      <div className="tx-lang-row">
        <span className="tx-lang-side">Deine Sprache</span>
        <button className="tx-lang-pick">
          <span><span className="tx-flag">🇩🇪</span>Deutsch</span>
          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </button>
      </div>
      <div className="settings-section-title" style={{ marginTop: 14 }}>Untertitel-Größe</div>
      <div className="tx-cap-sizes">
        <SizeRow label="Kunde" min={16} max={36} value={capSizeCustomer} onChange={(v) => setCapSize('customer', v)} unit="" />
        <SizeRow label="Du" min={11} max={22} value={capSizeSelf} onChange={(v) => setCapSize('self', v)} unit="" />
        <SizeRow label="Breite" min={40} max={92} value={capWidthPercent} onChange={setCapWidth} unit="%" />
      </div>
    </div>
  );
}


// pre170: Erreichbarkeit als eigener Tab. Jeder Hintergrund-Mechanismus ist einzeln
// abschaltbar (Default: alle an), wird erklärt (Was/Warum) und zeigt seinen aktuell
// im Browser erkannten Zustand.
const REACH_ITEMS: { key: ReachPrefKey; cap: keyof Capabilities; name: string; what: string; why: string }[] = [
  { key: 'notifications', cap: 'notifications', name: 'Benachrichtigungen',
    what: 'Zeigt eine System-Benachrichtigung, sobald ein Videocall eingeht.',
    why: 'Damit ein Ruf auch auffällt, wenn das Fenster im Hintergrund oder minimiert ist.' },
  { key: 'serviceWorker', cap: 'serviceWorker', name: 'Hintergrund-Dienst (Service Worker)',
    what: 'Kleiner Browser-Prozess, der auch bei geschlossenem Tab weiterläuft.',
    why: 'Grundlage dafür, dass Push-Rufe ankommen, ohne dass die App geöffnet sein muss.' },
  { key: 'webPush', cap: 'webPush', name: 'Push-Benachrichtigung',
    what: 'Der Server kann den Arbeitsplatz aktiv „anklopfen".',
    why: 'Stellt eingehende Rufe zuverlässig zu, auch wenn die App nicht im Vordergrund ist.' },
  { key: 'wakeLock', cap: 'wakeLock', name: 'Bildschirm wach halten',
    what: 'Verhindert, dass Bildschirm und Tab in den Ruhezustand gehen, solange „Verfügbar".',
    why: 'Ein schlafender Tab nimmt keine Rufe an — so bleibt der Arbeitsplatz aktiv erreichbar.' },
  { key: 'pwaInstalled', cap: 'pwaInstalled', name: 'Als App installiert',
    what: 'Die Anwendung läuft als installierte App in einem eigenen Fenster.',
    why: 'Installiert ist die Erreichbarkeit stabiler (eigenes Fenster, zuverlässigere Hinweise).' },
  { key: 'audioHeartbeat', cap: 'audioHeartbeat', name: 'Audio-Heartbeat (Backup)',
    what: 'Ein lautloses Tonsignal, das die Sitzung wachhält.',
    why: 'Letzte Absicherung gegen verpasste Rufe, falls Push/Benachrichtigungen blockiert sind.' },
];

function capBadge(state: CapabilityState): { label: string; color: string; bg: string } {
  switch (state) {
    case 'ok': return { label: 'aktiv', color: 'var(--status-available)', bg: '#22c55e1a' };
    case 'bad': return { label: 'blockiert', color: 'var(--danger)', bg: 'var(--danger-soft)' };
    case 'miss': return { label: 'nicht aktiv', color: 'var(--warning)', bg: 'var(--warning-soft)' };
    default: return { label: 'wird geprüft', color: 'var(--muted)', bg: 'var(--bg-elev-2)' };
  }
}

function AvailabilitySettings() {
  const { caps, overall } = useReachability();
  const reachPrefs = useUIStore((s) => s.reachPrefs);
  const setReachPref = useUIStore((s) => s.setReachPref);
  const overallLabel = overall === 'green' ? 'Erreichbar' : overall === 'yellow' ? 'Eingeschränkt erreichbar' : 'Nicht erreichbar';
  const overallColor = overall === 'green' ? 'var(--status-available)' : overall === 'yellow' ? 'var(--warning)' : 'var(--danger)';
  return (
    <div className="settings-section" style={{ borderBottom: 'none' }}>
      <div className="settings-section-title" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span>Erreichbarkeit im Hintergrund</span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: overallColor, textTransform: 'none', letterSpacing: 0, fontWeight: 600 }}>
          <span style={{ width: 7, height: 7, borderRadius: '50%', background: 'currentColor' }} />
          {overallLabel}
        </span>
      </div>
      <div style={{ fontSize: 11.5, color: 'var(--muted)', lineHeight: 1.5, marginBottom: 12 }}>
        Diese Mechanismen sorgen dafür, dass eingehende Rufe zuverlässig ankommen — auch wenn die App im Hintergrund läuft. Standardmäßig sind alle aktiv; einzelne lassen sich hier abschalten.
      </div>
      {REACH_ITEMS.map((it) => {
        const b = capBadge(caps[it.cap]);
        const on = reachPrefs[it.key];
        return (
          <div className="settings-row" key={it.key}>
            <div className="sr-info" style={{ paddingRight: 14 }}>
              <div className="sr-name" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {it.name}
                <span style={{ fontSize: 9, fontWeight: 600, letterSpacing: 0.4, textTransform: 'uppercase', color: b.color, background: b.bg, border: `1px solid ${b.color}33`, padding: '1px 6px', borderRadius: 999 }}>{b.label}</span>
              </div>
              <div className="sr-desc" style={{ lineHeight: 1.5 }}>
                {it.what}<br />
                <span style={{ opacity: 0.8 }}><strong style={{ fontWeight: 600 }}>Warum wichtig:</strong> {it.why}</span>
              </div>
            </div>
            <Toggle on={on} onClick={() => setReachPref(it.key, !on)} />
          </div>
        );
      })}
    </div>
  );
}


export function SettingsDrawer() {
  const open = useUIStore((s) => s.settingsOpen);
  const toggle = useUIStore((s) => s.toggleSettings);
  const theme = useUIStore((s) => s.theme);
  const setTheme = useUIStore((s) => s.setTheme);
  const audio = useUIStore((s) => s.audioPrefs);
  const setAudio = useUIStore((s) => s.setAudioPref);
  const setRingtone = useUIStore((s) => s.setRingtone);
  const companionConnected = useUIStore((s) => s.companionConnected);
  const tab = useUIStore((s) => s.settingsTab);
  const setTab = useUIStore((s) => s.setSettingsTab);

  // Esc schließt das Modal; beim Schließen zurück auf „Kamera & Geräte" (stoppt die Kamera).
  useEffect(() => {
    if (!open) { setTab('devices'); return; }
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') toggle(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, toggle, setTab]);

  return (
    <>
      <div className={`settings-modal-backdrop ${open ? 'active' : ''}`} onClick={toggle} />
      <div className={`settings-modal ${open ? 'active' : ''}`} role="dialog" aria-modal="true">
        <div className="settings-modal-head">
          <div className="settings-head-top">
            <div className="settings-title">Einstellungen</div>
            <button className="icon-btn" onClick={toggle} title="Schließen">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </button>
          </div>
          <div className="settings-tabs">
            <button className={`settings-tab ${tab === 'general' ? 'active' : ''}`} onClick={() => setTab('general')}>Allgemein</button>
            <button className={`settings-tab ${tab === 'reach' ? 'active' : ''}`} onClick={() => setTab('reach')}>Erreichbarkeit</button>
            <button className={`settings-tab ${tab === 'devices' ? 'active' : ''}`} onClick={() => setTab('devices')}>Kamera &amp; Geräte</button>
            <button className={`settings-tab ${tab === 'storage' ? 'active' : ''}`} onClick={() => setTab('storage')}>Ablage &amp; Ordner</button>
          </div>
        </div>

        <div className="settings-modal-body">
          {tab === 'general' && (
            <>
              <div className="settings-section">
                <div className="settings-section-title">Darstellung</div>
                <Row name="Theme" desc="Wird im Profil gespeichert">
                  <div className="theme-switch">
                    <button className={theme === 'dark' ? 'active' : ''} onClick={() => setTheme('dark')}>
                      Dark
                    </button>
                    <button className={theme === 'light' ? 'active' : ''} onClick={() => setTheme('light')}>
                      Light
                    </button>
                  </div>
                </Row>
              </div>

              <div className="settings-section">
                <div className="settings-section-title">Audio-Feedback</div>
                <Row name="Klingelton bei eingehendem Call" desc="Ton bei eingehendem Videocall">
                  <Toggle on={audio.ringtone} onClick={() => setAudio('ringtone', !audio.ringtone)} />
                </Row>
                {audio.ringtone && (
                  <div className="settings-row" style={{ alignItems: 'center' }}>
                    <div className="sr-info">
                      <div className="sr-name">Klingelton-Auswahl</div>
                      <div className="sr-desc">Ton wählen und anhören</div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <select
                        value={audio.ringtoneId}
                        onChange={(e) => setRingtone(e.target.value)}
                        style={{ background: 'var(--bg-elev-2)', border: '1px solid var(--hairline)', color: 'var(--fg)', borderRadius: 6, padding: '6px 8px', fontFamily: 'inherit', fontSize: 12.5, cursor: 'pointer' }}
                      >
                        {RINGTONES.map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
                      </select>
                      <button
                        onClick={() => playRingtonePreview(audio.ringtoneId)}
                        title="Anhören"
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: 'var(--bg-elev-2)', border: '1px solid var(--hairline)', color: 'var(--fg-soft)', borderRadius: 6, padding: '6px 10px', fontFamily: 'inherit', fontSize: 11.5, fontWeight: 600, cursor: 'pointer' }}
                      >
                        <svg viewBox="0 0 24 24" width="13" height="13" fill="currentColor"><path d="M8 5v14l11-7z" /></svg>
                        Anhören
                      </button>
                    </div>
                  </div>
                )}
                <Row name="Anruf angenommen" desc="Kurzer Klick bei Annahme">
                  <Toggle on={audio.accept} onClick={() => setAudio('accept', !audio.accept)} />
                </Row>
                <Row name="Auflegen" desc="Kurzer Klick beim Auflegen">
                  <Toggle on={audio.hangup} onClick={() => setAudio('hangup', !audio.hangup)} />
                </Row>
                <Row name="Snapshot" desc="Leiser Verschluss beim Snapshot">
                  <Toggle on={audio.snap} onClick={() => setAudio('snap', !audio.snap)} />
                </Row>
              </div>

              <div className="settings-section">
                <div className="settings-section-title">Companion</div>
                <Row
                  name="Lokale Ablage-Sitzung"
                  desc="Zweite Sitzung auf dem Terminal-Server für lokale Ablage (Aufzeichnung, Scan, Unterschrift, NFC) — ohne eigenes Audio/Video."
                >
                  <span className={`tx-state-pill ${companionConnected ? 'active' : ''}`}>
                    {companionConnected && <span className="tx-state-dot"></span>}
                    {companionConnected ? 'verbunden' : 'nicht verbunden'}
                  </span>
                </Row>
              </div>

              <TranslationSettings />

              <TelephonyLinkSection />
            </>
          )}

          {tab === 'reach' && <AvailabilitySettings />}

          {tab === 'devices' && (
            <div className="settings-section" style={{ borderBottom: 'none' }}>
              {open && <DeviceCheckPanel />}
            </div>
          )}

          {tab === 'storage' && (
            <div className="settings-section" style={{ borderBottom: 'none' }}>
              <div className="settings-section-title">Ablage &amp; Ordner</div>
              <div style={{ fontSize: 11.5, color: 'var(--muted)', background: 'var(--bg-elev-2)', border: '1px solid var(--hairline)', borderRadius: 8, padding: '8px 12px', lineHeight: 1.5, marginBottom: 10 }}>
                🔒 Zielordner und Empfänger-Schlüssel gelten geräteweit für diesen Browser. Die Ablage erfasster Dokumente erfolgt ausschließlich lokal — nichts wird an den Server übertragen.
              </div>
              {open && <FolderTargetsSettings />}
              {open && <SealCertSettings />}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

// ── pre250: Headset-Kopplung (Telefonstatus per WebHID, Zero-Install) ─────────
function HeadsetSettings() {
  const [hs, setHs] = useState(() => headsetState());
  useEffect(() => onHeadsetChange(setHs), []);
  if (!hs.supported) {
    return (
      <section style={{ marginTop: 22 }}>
        <div style={{ fontSize: 12, letterSpacing: '.08em', color: 'var(--muted,#8aa)', marginBottom: 8 }}>HEADSET-KOPPLUNG (OHNE INSTALLATION)</div>
        <div style={{ fontSize: 13, color: 'var(--muted,#8aa)' }}>
          In diesem Browser nicht verfügbar (WebHID erforderlich — Chrome oder Edge verwenden).
        </div>
      </section>
    );
  }
  return (
    <section style={{ marginTop: 22 }}>
      <div style={{ fontSize: 12, letterSpacing: '.08em', color: 'var(--muted,#8aa)', marginBottom: 8 }}>HEADSET-KOPPLUNG (OHNE INSTALLATION)</div>
      <div style={{ fontSize: 12.5, lineHeight: 1.55, marginBottom: 10, color: 'var(--muted,#8aa)' }}>
        <b>Was ist das?</b> Ihr USB-Headset (z. B. Jabra, Poly, EPOS) meldet dem Computer, ob gerade ein
        Gespräch läuft — diesen Zustand liest die Anwendung direkt am Headset mit. Es wird nichts
        installiert; die Kopplung gilt nur für diesen Arbeitsplatz und diesen Browser.
        {' '}<b>Was passiert dann?</b> Nimmt Ihr Telefon-Programm (z. B. Avaya) einen Anruf über das Headset
        an, sind Sie hier automatisch „am Telefon" und erhalten keine Videocalls. Nach dem Auflegen sind Sie
        wieder erreichbar. <b>Einrichtung:</b> einmal auf „Headset verbinden" klicken und das Headset im
        Browser-Dialog auswählen — fertig.
      </div>
      {hs.connected ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 13.5 }}>
            <b style={{ color: '#7ee787' }}>Gekoppelt:</b> {hs.deviceName}
            {' — '}
            {hs.offHook ? <b style={{ color: '#ffb74d' }}>im Gespräch (abgehoben)</b> : <span>aufgelegt</span>}
          </span>
          <button onClick={() => void disconnectHeadset()}
            style={{ padding: '7px 12px', borderRadius: 8, border: '1px solid var(--hairline,#444)', background: 'transparent', color: 'inherit', cursor: 'pointer', fontSize: 13 }}>Trennen</button>
        </div>
      ) : (
        <button onClick={() => void connectHeadset()}
          style={{ padding: '9px 14px', borderRadius: 8, border: 'none', background: 'var(--accent,#e2001a)', color: '#fff', fontWeight: 600, cursor: 'pointer', fontSize: 13.5 }}>
          Headset verbinden
        </button>
      )}
      {hs.connected && (
        <div style={{ marginTop: 14 }}>
          <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 12.5, cursor: 'pointer' }}>
            <input type="checkbox" defaultChecked={localStorage.getItem(LS_BUSYLIGHT) === '1'}
              onChange={(e) => localStorage.setItem(LS_BUSYLIGHT, e.target.checked ? '1' : '0')}
              style={{ marginTop: 2 }} />
            <span>
              <b>Besetztlicht bei Videoberatung</b> — während einer Videoberatung schaltet die Anwendung das
              rote Besetztlicht Ihres Headsets ein, damit Kolleginnen und Kollegen sehen, dass Sie im Gespräch
              sind. Am Ende geht es automatisch wieder aus.
              {!busylightAvailable() && <span style={{ color: '#ffb74d' }}> (Bei diesem Headset-Modell nicht ansteuerbar.)</span>}
            </span>
          </label>
        </div>
      )}
      <div style={{ marginTop: 14 }}>
        <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 4 }}>Wähl-Codes bei Videoberatung (für Anrufverteilung/KSC)</div>
        <div style={{ fontSize: 12.5, color: 'var(--muted,#8aa)', lineHeight: 1.55, marginBottom: 8 }}>
          <b>Wozu?</b> In Kundenservice-Centern verteilt die Telefonanlage Anrufe automatisch an freie
          Mitarbeiter. Damit Sie während einer Videoberatung keine Telefonanrufe zugeteilt bekommen,
          kann die Anwendung beim <b>Annehmen</b> eines Videocalls einen Abmelde-Code (meist „AUX",
          z. B. *44) und beim <b>Beenden</b> einen Anmelde-Code (z. B. *45) über Ihr Telefon-Programm
          wählen — so, als hätten Sie die Codes selbst getippt. Die gültigen Codes erhalten Sie von Ihrer
          Telefonie-Betreuung. Felder leer lassen = Funktion aus. Hinweis: Ihr Browser fragt beim ersten
          Mal, ob er das Telefon-Programm öffnen darf — dort „immer erlauben" wählen.
        </div>
        <details style={{ marginBottom: 10 }}>
          <summary style={{ fontSize: 12.5, cursor: 'pointer', color: 'var(--accent,#e2001a)', fontWeight: 600 }}>
            Welche Codes gelten bei welcher Telefonanlage? (Übersicht)
          </summary>
          <div style={{ fontSize: 12, lineHeight: 1.55, color: 'var(--muted,#8aa)', marginTop: 8 }}>
            <b>Wichtig:</b> Die Codes sind fast immer je Anlage individuell eingerichtet — verbindlich ist
            allein die Auskunft Ihrer Telefonie-Betreuung. Zur Orientierung:
            <table style={{ width: '100%', marginTop: 8, borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ textAlign: 'left', borderBottom: '1px solid var(--hairline,#444)' }}>
                  <th style={{ padding: '4px 6px' }}>Anlage</th>
                  <th style={{ padding: '4px 6px' }}>Funktion</th>
                  <th style={{ padding: '4px 6px' }}>Codes</th>
                </tr>
              </thead>
              <tbody>
                <tr style={{ borderBottom: '1px solid var(--hairline,#333)' }}>
                  <td style={{ padding: '4px 6px', verticalAlign: 'top' }}>Avaya Aura (KSC, Elite)</td>
                  <td style={{ padding: '4px 6px', verticalAlign: 'top' }}>„AUX Work" (abmelden) / „Auto-In" (anmelden)</td>
                  <td style={{ padding: '4px 6px', verticalAlign: 'top' }}>Keine Werkseinstellung — wird je Anlage frei vergeben; beim AUX-Code wird oft eine Grund-Ziffer angehängt (z. B. Code + 7 für „Videoberatung").</td>
                </tr>
                <tr style={{ borderBottom: '1px solid var(--hairline,#333)' }}>
                  <td style={{ padding: '4px 6px', verticalAlign: 'top' }}>Avaya IP Office</td>
                  <td style={{ padding: '4px 6px', verticalAlign: 'top' }}>Sammelanschluss ab-/anmelden</td>
                  <td style={{ padding: '4px 6px', verticalAlign: 'top' }}>Häufige Voreinstellung *36 (abmelden) / *35 (anmelden); „Nicht stören" oft *08 / *09 — installationsabhängig.</td>
                </tr>
                <tr style={{ borderBottom: '1px solid var(--hairline,#333)' }}>
                  <td style={{ padding: '4px 6px', verticalAlign: 'top' }}>3CX</td>
                  <td style={{ padding: '4px 6px', verticalAlign: 'top' }}>Warteschleifen ab-/anmelden</td>
                  <td style={{ padding: '4px 6px', verticalAlign: 'top' }}>Voreinstellung meist *63 (abmelden) / *62 (anmelden) — vom Admin änderbar.</td>
                </tr>
                <tr style={{ borderBottom: '1px solid var(--hairline,#333)' }}>
                  <td style={{ padding: '4px 6px', verticalAlign: 'top' }}>Mitel / Unify OpenScape</td>
                  <td style={{ padding: '4px 6px', verticalAlign: 'top' }}>„Make Busy" / Gruppen-Abwesenheit bzw. Kennzahlen-Prozeduren</td>
                  <td style={{ padding: '4px 6px', verticalAlign: 'top' }}>Kennzahlen je Anlage konfiguriert — bitte erfragen.</td>
                </tr>
                <tr style={{ borderBottom: '1px solid var(--hairline,#333)' }}>
                  <td style={{ padding: '4px 6px', verticalAlign: 'top' }}>Estos ProCall</td>
                  <td style={{ padding: '4px 6px', verticalAlign: 'top' }}>—</td>
                  <td style={{ padding: '4px 6px', verticalAlign: 'top' }}>Nutzt die Codes der darunterliegenden Telefonanlage (siehe deren Zeile).</td>
                </tr>
                <tr>
                  <td style={{ padding: '4px 6px', verticalAlign: 'top' }}>Cisco / Microsoft Teams / Webex</td>
                  <td style={{ padding: '4px 6px', verticalAlign: 'top' }}>—</td>
                  <td style={{ padding: '4px 6px', verticalAlign: 'top' }}>Kein Wählcode-Verfahren: Teams/Webex oben direkt verbinden; bei Cisco-Anlagen den Konnektor Ihres Instituts nutzen.</td>
                </tr>
              </tbody>
            </table>
          </div>
        </details>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <label style={{ fontSize: 12.5 }}>Code bei Annahme (abmelden):{' '}
            <input defaultValue={localStorage.getItem(LS_FAC_AUX) ?? ''} placeholder="z. B. *44"
              onChange={(e) => localStorage.setItem(LS_FAC_AUX, e.target.value.trim())}
              style={{ padding: '6px 9px', borderRadius: 6, background: 'var(--bg-elev-2,#20262b)', color: 'inherit', border: '1px solid var(--hairline,#444)', width: 100, fontSize: 13 }} />
          </label>
          <label style={{ fontSize: 12.5 }}>Code bei Ende (anmelden):{' '}
            <input defaultValue={localStorage.getItem(LS_FAC_READY) ?? ''} placeholder="z. B. *45"
              onChange={(e) => localStorage.setItem(LS_FAC_READY, e.target.value.trim())}
              style={{ padding: '6px 9px', borderRadius: 6, background: 'var(--bg-elev-2,#20262b)', color: 'inherit', border: '1px solid var(--hairline,#444)', width: 100, fontSize: 13 }} />
          </label>
        </div>
      </div>
    </section>
  );
}
